/**
 * Formats a number using the specified format.
 * @param format - The format to use for the number. Default is 'en'.
 * @param number - The number to format.
 * @returns The formatted number.
 */
export const formatNumber = (format: string, number: number) => {
  return Intl.NumberFormat(format).format(number);
};

/**
 * Formats a number with thousands notation for values over 1000.
 * For example, 1,100 is formatted as '1.1K'.
 * @param number - The number to format.
 * @returns The formatted number string.
 */
export const formatBigNumbers = (number: number): string => {
  if (number === null || number === undefined) {
    return '0';
  }

  if (number < 1000) {
    return number.toString();
  }

  //TODO: tests for this
  const formatted = (value: number) => {
    const rounded = Math.round(value * 10) / 10;
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
  };

  if (number < 1_000_000) {
    const value = number / 1000;
    const formattedValue = formatted(value);
    return `${formattedValue}K`;
  } else {
    const value = number / 1_000_000;
    const formattedValue = formatted(value);
    return `${formattedValue}M`;
  }
};

interface FormatPercentageOptions {
  round?: boolean;
  decimals?: number;
}

export const formatPercentage = (number: number, options: FormatPercentageOptions = {}): number => {
  const { round = true, decimals = 0 } = options;
  const value = number || 0;

  if (round) return Math.round(value);
  return Number(value.toFixed(decimals));
};

export const formatPercentageTwoDp = (number: number) => {
  const value = number || 0;
  const rounded = Number(value.toFixed(2));

  // < 0.01 when percentage is 0.00 when rounded to 2 dp
  if (rounded === 0 && value > 0) {
    return '< 0.01';
  }

  return rounded.toFixed(2);
};
